/**
 * EM Hub design-system components — reusable, inline-styled primitives that
 * recreate the design handoff's look, mapped to the app's brand orange.
 * Used by the redesigned Employees / Workspace / Attendance / etc. pages.
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown } from "lucide-react";
import { T, RAW, avatarColor, initialsOf } from "./tokens";

export { T, RAW, avatarColor, initialsOf };

// ── Page shell ────────────────────────────────────────────────────────────────
export const Page = ({ children, max = 1440, style = {} }) => (
  <div
    style={{
      maxWidth: max,
      margin: "0 auto",
      padding: "0 32px 48px",
      position: "relative",
      ...style,
    }}
  >
    {children}
  </div>
);

export const PageHeader = ({ icon: Icon, title, subtitle, actions }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 16,
      margin: "28px 0 20px",
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 14,
        background: T.orange50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {Icon && <Icon size={24} color={T.orange} />}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <h1
        style={{
          margin: 0,
          font: `700 28px/34px ${T.font}`,
          letterSpacing: "-0.025em",
          color: T.fg1,
        }}
      >
        {title}
      </h1>
      {subtitle != null && (
        <p
          style={{
            margin: "3px 0 0",
            font: `400 13px/18px ${T.font}`,
            color: T.fg5,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
    {actions && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
    )}
  </div>
);

// ── Card ──────────────────────────────────────────────────────────────────────
export const Card = ({ children, padding = 20, style = {}, ...rest }) => (
  <div
    {...rest}
    style={{
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderRadius: T.rLg,
      padding,
      boxShadow: T.shadowSm,
      position: "relative",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── Buttons ───────────────────────────────────────────────────────────────────
const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  font: `500 14px/1 ${T.font}`,
  whiteSpace: "nowrap",
};

export const PrimaryButton = ({ children, icon: Icon, style = {}, ...p }) => (
  <button
    {...p}
    style={{
      ...btnBase,
      padding: "10px 16px",
      borderRadius: T.rLg,
      border: 0,
      background: T.gradient,
      color: "#fff",
      boxShadow: T.shadowBrand,
      ...style,
    }}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

export const SecondaryButton = ({ children, icon: Icon, style = {}, ...p }) => (
  <button
    {...p}
    style={{
      ...btnBase,
      padding: "9px 14px",
      borderRadius: T.rMd,
      border: `1px solid ${T.borderSoft}`,
      background: "var(--em-bg-card)",
      color: T.fg1,
      ...style,
    }}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

export const DarkButton = ({ children, icon: Icon, style = {}, ...p }) => (
  <button
    {...p}
    style={{
      ...btnBase,
      justifyContent: "center",
      padding: "10px 16px",
      borderRadius: T.rMd,
      border: 0,
      background: T.bgBlack,
      color: "#fff",
      ...style,
    }}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

export const GhostButton = ({ children, icon: Icon, style = {}, ...p }) => (
  <button
    {...p}
    style={{
      ...btnBase,
      gap: 6,
      padding: "8px 10px",
      borderRadius: T.rMd,
      border: 0,
      background: "transparent",
      color: T.fg4,
      font: `500 13px/1 ${T.font}`,
      ...style,
    }}
  >
    {Icon && <Icon size={14} />}
    {children}
  </button>
);

export const IconButton = ({
  icon: Icon,
  tone = "default",
  title,
  style = {},
  ...p
}) => {
  const tones = {
    default: {
      border: `1px solid ${T.border}`,
      bg: "transparent",
      color: T.fg4,
    },
    danger: {
      border: `1px solid rgba(231,0,11,0.2)`,
      bg: "rgba(231,0,11,0.04)",
      color: T.danger,
    },
    purple: {
      border: `1px solid rgba(142,81,255,0.3)`,
      bg: "rgba(142,81,255,0.08)",
      color: T.purple500,
    },
  };
  const t = tones[tone] || tones.default;
  return (
    <button
      {...p}
      title={title}
      style={{
        padding: 0,
        width: 28,
        height: 28,
        borderRadius: T.rSm,
        border: t.border,
        background: t.bg,
        color: t.color,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {Icon && <Icon size={13} />}
    </button>
  );
};

// Flat icon action (matches the Teams-page edit/delete icons) + a custom
// on-brand hover tooltip. Use for row/table actions. `label` is the tooltip
// text (and the accessible name). Extra props (onClick, disabled…) pass through
// to the <button>.
export const IconAction = ({ icon: Icon, label, size = 14, color, style = {}, ...p }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        {...p}
        type="button"
        aria-label={label}
        style={{
          background: "none",
          border: 0,
          padding: 2,
          display: "flex",
          cursor: p.disabled ? "not-allowed" : "pointer",
          color: color || T.fg7,
          opacity: p.disabled ? 0.4 : 1,
          ...style,
        }}
      >
        {Icon && <Icon size={size} />}
      </button>
      {hover && label && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 7px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1f2937",
            color: "#fff",
            font: `500 11px ${T.font}`,
            lineHeight: 1.3,
            padding: "4px 8px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 100,
            boxShadow: "0 6px 16px rgba(0,0,0,0.22)",
          }}
        >
          {label}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "4px solid #1f2937",
            }}
          />
        </span>
      )}
    </span>
  );
};

// ── Chips / badges ─────────────────────────────────────────────────────────────
export const Chip = ({
  children,
  bg = T.bgMuted,
  color = T.fg5,
  border,
  style = {},
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: T.pill,
      background: bg,
      color,
      border: border || "none",
      font: `600 11px/1.4 ${T.font}`,
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </span>
);

// Status badge with a small palette resolved by lowercased label.
const STATUS_PALETTE = {
  active: { bg: "#dcfce7", color: T.success600 },
  confirmed: { bg: "#dcfce7", color: T.success600 },
  present: { bg: "#dcfce7", color: T.success600 },
  approved: { bg: "#dcfce7", color: T.success600 },
  published: { bg: "#dcfce7", color: T.success600 },
  done: { bg: "#dcfce7", color: T.success600 },
  completed: { bg: "#dbeafe", color: T.info600 },
  generated: { bg: "#dbeafe", color: T.info600 },
  sent: { bg: "#dcfce7", color: T.success600 },
  "in progress": { bg: "#dbeafe", color: T.info600 },
  in_progress: { bg: "#dbeafe", color: T.info600 },
  working: { bg: "#dbeafe", color: T.info600 },
  probation: { bg: T.warn50, color: "#9a3412" },
  pending: { bg: T.warn50, color: "#9a3412" },
  draft: { bg: T.warn50, color: "#9a3412" },
  review: { bg: T.warn50, color: "#9a3412" },
  notice: { bg: "#ffe2c7", color: "#9a3412" },
  "notice period": { bg: "#ffe2c7", color: "#9a3412" },
  "on leave": { bg: "#e0f2fe", color: T.info600 },
  planning: { bg: T.purple50, color: T.purple },
  todo: { bg: T.bgMuted, color: T.fg5 },
  inactive: { bg: T.bgMuted, color: T.fg5 },
  archived: { bg: T.bgMuted, color: T.fg5 },
  deleted: { bg: "#fee2e2", color: T.danger },
  terminated: { bg: "#fee2e2", color: T.danger },
  resigned: { bg: "#fee2e2", color: T.danger },
  rejected: { bg: "#fee2e2", color: T.danger },
  absent: { bg: "#fee2e2", color: T.danger },
  on_hold: { bg: T.warn50, color: "#9a3412" },
};
export const StatusBadge = ({ label, value }) => {
  const text = label || value;
  if (!text) return <span style={{ color: T.fg7 }}>—</span>;
  const p = STATUS_PALETTE[String(text).toLowerCase().trim()] || {
    bg: T.purple50,
    color: T.purple,
  };
  return (
    <span
      style={{
        padding: "2px 10px",
        borderRadius: T.pill,
        background: p.bg,
        color: p.color,
        font: `600 11px ${T.font}`,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

// ── Avatar ──────────────────────────────────────────────────────────────────────
export const Avatar = ({ name = "", photo, size = 32, gradient = false }) => {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: T.pill,
          objectFit: "cover",
          border: `1px solid ${T.border}`,
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: T.pill,
        flexShrink: 0,
        background: gradient ? T.gradient : avatarColor(name),
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        font: `600 ${Math.round(size * 0.36)}px ${T.font}`,
      }}
    >
      {initialsOf(name)}
    </div>
  );
};

// ── Form atoms ────────────────────────────────────────────────────────────────
export const Label = ({ children, hint }) => (
  <label
    style={{
      display: "block",
      font: `500 12px/1.4 ${T.font}`,
      color: T.fg3,
      marginBottom: 6,
    }}
  >
    {children}
    {hint && (
      <span style={{ marginLeft: 6, color: T.fg7, fontWeight: 400 }}>
        {hint}
      </span>
    )}
  </label>
);

export const Input = ({ style = {}, ...p }) => (
  <input
    {...p}
    style={{
      width: "100%",
      padding: "9px 12px",
      border: `1px solid ${T.border}`,
      borderRadius: T.rMd,
      background: "var(--em-bg-card)",
      font: `400 13px/1.4 ${T.font}`,
      color: T.fg1,
      outline: "none",
      boxSizing: "border-box",
      ...style,
    }}
  />
);

export const SELECT_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2399a1af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")";

// Drop-in style fragment that gives any bare <select> the design system's
// dropdown chevron. Spread it after the field's own styles:
//   <select style={{ ...myInput, ...selectChevron }}>
// `appearance:none` removes each browser's native arrow so only ours shows,
// and the right padding keeps long option text from running under it.
export const selectChevron = {
  appearance: "none",
  cursor: "pointer",
  paddingRight: 30,
  backgroundImage: SELECT_BG,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
};
export const Select = ({ style = {}, children, ...p }) => (
  <select
    {...p}
    style={{
      width: "100%",
      padding: "9px 30px 9px 12px",
      border: `1px solid ${T.border}`,
      borderRadius: T.rMd,
      background: "var(--em-bg-card)",
      font: `500 13px/1.4 ${T.font}`,
      color: T.fg1,
      outline: "none",
      boxSizing: "border-box",
      appearance: "none",
      cursor: "pointer",
      backgroundImage: SELECT_BG,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 10px center",
      ...style,
    }}
  >
    {children}
  </select>
);

export const SearchInput = ({
  value,
  onChange,
  placeholder = "Search…",
  style = {},
}) => (
  <div style={{ position: "relative", ...style }}>
    <Search
      size={14}
      style={{
        position: "absolute",
        left: 10,
        top: "50%",
        transform: "translateY(-50%)",
        color: T.fg7,
      }}
    />
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "9px 12px 9px 32px",
        border: `1px solid ${T.border}`,
        borderRadius: T.rMd,
        background: "var(--em-bg-card)",
        font: `400 13px ${T.font}`,
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  </div>
);

// ── Tabs ──────────────────────────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange, style = {} }) => (
  <div
    style={{
      display: "inline-flex",
      gap: 2,
      padding: 4,
      background: T.bgTab,
      borderRadius: T.rXl,
      ...style,
    }}
  >
    {tabs.map((t) => {
      const on = active === t.key;
      const Icon = t.icon;
      return (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            borderRadius: T.rLg,
            border: 0,
            cursor: "pointer",
            background: on ? T.bgCard : "transparent",
            color: on ? T.orange : T.fg5,
            font: `${on ? 600 : 500} 13px ${T.font}`,
            boxShadow: on ? T.shadowSm : "none",
            transition: "all .15s",
          }}
        >
          {Icon && <Icon size={15} />}
          {t.label}
        </button>
      );
    })}
  </div>
);

// ── Empty state ──────────────────────────────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, subtitle, action }) => (
  <div style={{ padding: "56px 24px", textAlign: "center" }}>
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: T.pill,
        background: T.bgMuted,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
      }}
    >
      {Icon && <Icon size={28} color={T.fg7} />}
    </div>
    <div style={{ font: `600 16px ${T.font}`, color: T.fg1, marginBottom: 6 }}>
      {title}
    </div>
    {subtitle && (
      <div
        style={{ font: `400 13px ${T.font}`, color: T.fg5, marginBottom: 18 }}
      >
        {subtitle}
      </div>
    )}
    {action}
  </div>
);

// ── Pagination ───────────────────────────────────────────────────────────────
export const Pagination = ({ page, totalPages, total, pageSize, onPage }) => {
  if (totalPages <= 1) return null;
  // Up to 7 page buttons in a window that follows the current page, so pages
  // past 7 stay reachable and the current one is always highlighted.
  const windowSize = Math.min(7, totalPages);
  const firstPage = Math.max(
    1,
    Math.min(page - Math.floor(windowSize / 2), totalPages - windowSize + 1),
  );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 14,
        font: `400 13px ${T.font}`,
        color: T.fg5,
      }}
    >
      <span>
        Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}{" "}
        of {total}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1}
          style={{
            padding: "6px 10px",
            borderRadius: T.rMd,
            border: `1px solid ${T.border}`,
            background: page === 1 ? T.bgHead : T.bgCard,
            cursor: page === 1 ? "default" : "pointer",
            color: page === 1 ? T.fg7 : T.fg1,
            font: `500 13px ${T.font}`,
          }}
        >
          ← Prev
        </button>
        {Array.from({ length: windowSize }, (_, i) => firstPage + i).map(
          (p) => (
            <button
              key={p}
              onClick={() => onPage(p)}
              style={{
                width: 34,
                height: 34,
                borderRadius: T.rMd,
                border: `1px solid ${p === page ? T.orange : T.border}`,
                background: p === page ? T.orange50 : T.bgCard,
                cursor: "pointer",
                padding: "0",
                font: `${p === page ? 700 : 400} 13px ${T.font}`,
                color: p === page ? T.orange : T.fg1,
              }}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          style={{
            padding: "6px 10px",
            borderRadius: T.rMd,
            border: `1px solid ${T.border}`,
            background: page === totalPages ? T.bgHead : T.bgCard,
            cursor: page === totalPages ? "default" : "pointer",
            color: page === totalPages ? T.fg7 : T.fg1,
            font: `500 13px ${T.font}`,
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

// ── Modal shell ──────────────────────────────────────────────────────────────
export const ModalOverlay = ({ children, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(3,2,19,0.45)",
      // .main-header is a sticky element pinned at z-index: 1000 — an exact
      // tie here is fragile (which one wins depends on DOM order, not
      // anything explicit) and low enough that a tall modal's own header can
      // end up visually painted over by the app nav bar. 1200 clears it
      // outright and matches the shared .modal-overlay class.
      zIndex: 1200,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}
  >
    {children}
  </div>
);

/**
 * ConfirmDialog — the app's answer to window.confirm().
 *
 * A browser confirm blocks the whole tab, cannot be styled, and reads as a
 * "localhost:3000 says" system popup rather than part of the product. This
 * renders in a portal so a card with `backdrop-filter` (which becomes the
 * containing block for fixed positioning) can't trap it, closes on Escape or a
 * backdrop click, and focuses the confirm button so Enter still works.
 *
 * Props: open, title, message, confirmLabel, cancelLabel, danger, busy,
 *        onConfirm, onCancel.
 */
export const ConfirmDialog = ({
  open,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={() => !busy && onCancel?.()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,2,19,0.45)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 400,
          background: "var(--em-bg-card)",
          borderRadius: 14,
          boxShadow: T.shadowMd,
          padding: 22,
        }}
      >
        <h3 style={{ margin: "0 0 8px", font: `700 16px ${T.font}`, color: T.fg1 }}>
          {title}
        </h3>
        {message && (
          <p style={{ margin: "0 0 20px", font: `400 13px/19px ${T.font}`, color: T.fg5 }}>
            {message}
          </p>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "9px 16px",
              borderRadius: T.rMd,
              border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)",
              color: T.fg2,
              font: `600 13px ${T.font}`,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: "9px 16px",
              borderRadius: T.rMd,
              border: 0,
              background: danger ? T.danger : T.orange,
              color: "#fff",
              font: `600 13px ${T.font}`,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const ChevronDownIcon = ChevronDown;
